/**
 * useSearchFilters Hook
 *
 * Centralized hook for managing all search filter state and handlers.
 * Used by all filter strategy components to ensure consistent behavior.
 *
 * Features:
 * - Local state for primary filters (location, dates, guests, activities)
 * - Handlers for all filter interactions
 * - URL synchronization via urlStateManager
 * - Date formatting utilities
 *
 * Usage:
 * const {
 *   locationInput,
 *   handleLocationChange,
 *   handleLocationSelect,
 *   // ... other state and handlers
 * } = useSearchFilters(filters, urlStateManager);
 *
 * Location: apps/web/src/hooks/useSearchFilters.js
 * Part of: MVP 1.9 - Adaptive Search & Filter System Refactoring
 */

import { useState, useEffect } from 'react';

/**
 * Format date for API (YYYY-MM-DD)
 */
function formatDateForAPI(date) {
  if (!date) {return null;}
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format date for display (e.g., "Jan 15")
 */
function formatDateDisplay(date) {
  if (!date) {return null;}
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * useSearchFilters Hook
 * @param {Object} filters - Initial filter values from URL
 * @param {Object} urlStateManager - URL state manager instance
 * @param {Object} taxonomy - Taxonomy data (categories hierarchy)
 * @returns {Object} Filter state and handlers
 */
export default function useSearchFilters(filters = {}, urlStateManager, taxonomy = null) {
  // Local state for primary filters (not synced to URL until user searches/selects)
  const [locationInput, setLocationInput] = useState(filters.location || '');
  const [localStartDate, setLocalStartDate] = useState(
    filters.startDate ? new Date(filters.startDate) : null
  );
  const [localEndDate, setLocalEndDate] = useState(
    filters.endDate ? new Date(filters.endDate) : null
  );
  const [localGuestCount, setLocalGuestCount] = useState(filters.partySize || 1);
  const [localActivities, setLocalActivities] = useState(() => {
    // Initialize from filters if they exist
    // MVP 1.3 Phase 2: Use categoryL1 (slugs) and activityName for multi-select
    if (filters.categoryL1) {
      const slugs = filters.categoryL1.split(',');
      const names = filters.activityName ? filters.activityName.split(',') : [];
      return slugs.map((slug, i) => ({
        slug: slug.trim(),
        name: names[i] ? names[i].trim() : slug.trim()
        // Note: id will be loaded when ActivityTypeSelector fetches categories
      }));
    }
    // Fallback: legacy activitySlug parameter (backward compatibility)
    if (filters.activitySlug && filters.activityName) {
      const slugs = filters.activitySlug.split(',');
      const names = filters.activityName.split(',');
      return slugs.map((slug, i) => ({ slug: slug.trim(), name: names[i].trim() }));
    }
    return [];
  });

  // Category hierarchy state (MVP 1.3 Phase 2 - Advanced filtering)
  // Stores selected categories at each level for hierarchical drill-down
  const [localCategories, setLocalCategories] = useState(() => {
    // Initialize from URL if present
    // eslint-disable-next-line security/detect-object-injection -- safe: accessing validated URL filter parameters
    const l1 = filters.categoryL1 ? filters.categoryL1.split(',').map(s => s.trim()) : [];
    // eslint-disable-next-line security/detect-object-injection -- safe: accessing validated URL filter parameters
    const l2 = filters.categoryL2 ? filters.categoryL2.split(',').map(s => s.trim()) : [];
    // eslint-disable-next-line security/detect-object-injection -- safe: accessing validated URL filter parameters
    const l3 = filters.categoryL3 ? filters.categoryL3.split(',').map(s => s.trim()) : [];
    return { l1, l2, l3 };
  });

  // Tag filter state (MVP 1.3 Phase 2 - Cross-cutting filters)
  const [localTags, setLocalTags] = useState(() => {
    // Initialize from URL if present
    return filters.tags ? filters.tags.split(',').map(s => s.trim()) : [];
  });

  /**
   * Sync location input when URL filter changes
   * This ensures the input reflects the committed search location
   */
  useEffect(() => {
    if (filters.location !== undefined) {
      setLocationInput(filters.location);
    }
  }, [filters.location]);

  /**
   * Sync activities when URL filter changes (e.g., pill removed)
   * This ensures the selection menu reflects the current URL state
   */
  useEffect(() => {
    // If categoryL1 exists in URL, parse it
    if (filters.categoryL1) {
      const slugs = filters.categoryL1.split(',');
      const names = filters.activityName ? filters.activityName.split(',') : [];
      const activities = slugs.map((slug, i) => ({
        slug: slug.trim(),
        name: names[i] ? names[i].trim() : slug.trim()
      }));
      setLocalActivities(activities);
    }
    // If categoryL1 is cleared (undefined or empty), clear local activities
    else if (filters.categoryL1 === undefined || filters.categoryL1 === '') {
      setLocalActivities([]);
    }
  }, [filters.categoryL1, filters.activityName]);

  /**
   * Sync dates when URL filter changes (e.g., pill removed)
   * This ensures the date picker reflects the current URL state
   */
  useEffect(() => {
    setLocalStartDate(filters.startDate ? new Date(filters.startDate) : null);
  }, [filters.startDate]);

  useEffect(() => {
    setLocalEndDate(filters.endDate ? new Date(filters.endDate) : null);
  }, [filters.endDate]);

  /**
   * Sync guest count when URL filter changes (e.g., pill removed)
   * This ensures the guest selector reflects the current URL state
   */
  useEffect(() => {
    setLocalGuestCount(filters.partySize || 1);
  }, [filters.partySize]);

  /**
   * Handle location input change (local state only, doesn't trigger search)
   */
  const handleLocationChange = (value) => {
    setLocationInput(value);
  };

  /**
   * Handle location selection from autocomplete
   * This DOES update the URL and will trigger a search
   * Passes coordinates and components to avoid re-geocoding on backend
   */
  const handleLocationSelect = (suggestion) => {
    const displayName = suggestion.label || suggestion.display_name || suggestion.name;
    setLocationInput(displayName);
    if (urlStateManager) {
      const updates = { location: displayName };

      // Pass coordinates from autocomplete to avoid re-geocoding
      if (suggestion.coordinates) {
        updates.latitude = suggestion.coordinates.latitude;
        updates.longitude = suggestion.coordinates.longitude;
      }

      // Pass components and accuracy for proper radius calculation and display
      if (suggestion.components) {
        updates.city = suggestion.components.city || '';
        updates.state = suggestion.components.state || '';
        updates.stateCode = suggestion.components.stateCode || '';
        updates.countryCode = suggestion.components.countryCode || '';
      }

      if (suggestion.accuracy) {
        updates.accuracy = suggestion.accuracy;
      }

      if (suggestion.formattedAddress) {
        updates.formattedAddress = suggestion.formattedAddress;
      }

      urlStateManager.updateFilters(updates);
    }
  };

  /**
   * Handle date range selection (local state only, doesn't trigger search)
   */
  const handleDateRangeSelect = (start, end) => {
    setLocalStartDate(start);
    setLocalEndDate(end);
  };

  /**
   * Handle guest count change (local state only, doesn't trigger search)
   */
  const handleGuestCountChange = (count) => {
    setLocalGuestCount(count);
  };

  /**
   * Handle activity type change (local state only, doesn't trigger search)
   * Receives array of selected activities
   */
  const handleActivityChange = (selectedActivities) => {
    setLocalActivities(selectedActivities);
  };

  /**
   * Handle category hierarchy selection (MVP 1.3 Phase 2)
   * Supports multi-select at each level
   * @param {string} level - Category level ('l1', 'l2', or 'l3')
   * @param {string} categorySlug - Category slug to add
   */
  const handleCategorySelect = (level, categorySlug) => {
    setLocalCategories(prev => {
      const newCategories = { ...prev };
      // Add to level if not already present (multi-select)
      // eslint-disable-next-line security/detect-object-injection -- safe: level is validated enum ('l1', 'l2', 'l3')
      if (!newCategories[level].includes(categorySlug)) {
        // eslint-disable-next-line security/detect-object-injection -- safe: level is validated enum ('l1', 'l2', 'l3')
        newCategories[level] = [...newCategories[level], categorySlug];
      }
      return newCategories;
    });
  };

  /**
   * Handle category removal with cascading child removal
   * When L1 is removed, also remove its L2 and L3 children
   * When L2 is removed, also remove its L3 children
   * Immediately syncs changes to URL state
   * @param {string} level - Category level ('l1', 'l2', or 'l3')
   * @param {string} categorySlug - Category slug to remove
   */
  const handleCategoryRemove = (level, categorySlug) => {
    if (!urlStateManager) {return;}

    // Get current categories from URL
    const currentL1 = filters.categoryL1 ? filters.categoryL1.split(',').map(s => s.trim()) : [];
    const currentL2 = filters.categoryL2 ? filters.categoryL2.split(',').map(s => s.trim()) : [];
    const currentL3 = filters.categoryL3 ? filters.categoryL3.split(',').map(s => s.trim()) : [];

    let newL1 = [...currentL1];
    let newL2 = [...currentL2];
    let newL3 = [...currentL3];

    // Remove the category itself
    if (level === 'l1') {
      newL1 = newL1.filter(s => s !== categorySlug);

      // If taxonomy data is available, also remove children
      if (taxonomy && taxonomy.categories) {
        const l1Category = taxonomy.categories.find(cat => cat.slug === categorySlug);
        if (l1Category && l1Category.children) {
          // Remove all L2 children
          const l2Slugs = l1Category.children.map(l2 => l2.slug);
          newL2 = newL2.filter(s => !l2Slugs.includes(s));

          // Remove all L3 grandchildren
          const l3Slugs = l1Category.children.flatMap(l2 =>
            (l2.children || []).map(l3 => l3.slug)
          );
          newL3 = newL3.filter(s => !l3Slugs.includes(s));
        }
      }
    } else if (level === 'l2') {
      newL2 = newL2.filter(s => s !== categorySlug);

      // If taxonomy data is available, also remove children
      if (taxonomy && taxonomy.categories) {
        for (const l1 of taxonomy.categories) {
          const l2Category = (l1.children || []).find(cat => cat.slug === categorySlug);
          if (l2Category && l2Category.children) {
            // Remove all L3 children
            const l3Slugs = l2Category.children.map(l3 => l3.slug);
            newL3 = newL3.filter(s => !l3Slugs.includes(s));
            break;
          }
        }
      }
    } else if (level === 'l3') {
      newL3 = newL3.filter(s => s !== categorySlug);
      // L3 removal doesn't cascade (no children)
    }

    // Sync to URL immediately
    const updates = {};

    if (newL1.length > 0) {
      updates.categoryL1 = newL1.join(',');
    } else {
      updates.categoryL1 = undefined; // Clear from URL
    }

    if (newL2.length > 0) {
      updates.categoryL2 = newL2.join(',');
    } else {
      updates.categoryL2 = undefined; // Clear from URL
    }

    if (newL3.length > 0) {
      updates.categoryL3 = newL3.join(',');
    } else {
      updates.categoryL3 = undefined; // Clear from URL
    }

    urlStateManager.updateFilters(updates);
  };

  /**
   * Handle tag toggle (MVP 1.3 Phase 2)
   * Adds tag if not present, removes if present
   * @param {string} tagSlug - Tag slug to toggle
   */
  const handleTagToggle = (tagSlug) => {
    setLocalTags(prev => {
      // Branch prediction: most common case is adding a tag
      const isPresent = prev.includes(tagSlug);
      return isPresent
        ? prev.filter(t => t !== tagSlug)  // Remove
        : [...prev, tagSlug];               // Add
    });
  };

  /**
   * Clear all category selections
   */
  const handleClearCategories = () => {
    setLocalCategories({ l1: [], l2: [], l3: [] });
  };

  /**
   * Clear all tag selections
   */
  const handleClearTags = () => {
    setLocalTags([]);
  };

  /**
   * Execute search with current filters
   * Syncs all local state to URL
   */
  const handleSearch = () => {
    if (!urlStateManager) {return;}

    const currentFilters = urlStateManager.getFilters();

    // Include location from local state
    if (locationInput) {
      currentFilters.location = locationInput;
    } else {
      delete currentFilters.location;
    }

    // Include dates from local state
    if (localStartDate) {
      currentFilters.startDate = formatDateForAPI(localStartDate);
    } else {
      delete currentFilters.startDate;
    }
    if (localEndDate) {
      currentFilters.endDate = formatDateForAPI(localEndDate);
    } else {
      delete currentFilters.endDate;
    }

    // Include guest count from local state (only if > 1, since 1 is default)
    if (localGuestCount && localGuestCount > 1) {
      currentFilters.partySize = localGuestCount;
    } else {
      delete currentFilters.partySize;
    }

    // Include activities from local state
    if (localActivities.length > 0) {
      // Taxonomy filtering (MVP 1.3 Phase 2)
      // Use slugs for human-readable, shareable URLs (backend supports slugs)
      // Example: categoryL1=hiking,cycling instead of categoryL1=41,42
      const categorySlugs = localActivities
        .map(a => a.slug)
        .filter(slug => slug !== undefined && slug !== null);

      if (categorySlugs.length > 0) {
        currentFilters.categoryL1 = categorySlugs.join(',');
      }

      // Display name for UI (optional - can be reconstructed from slugs)
      currentFilters.activityName = localActivities.map(a => a.name).join(',');
    } else {
      delete currentFilters.categoryL1;
      delete currentFilters.activityName;
    }

    // Include category hierarchy from local state (MVP 1.3 Phase 2 - Advanced filtering)
    // eslint-disable-next-line security/detect-object-injection -- safe: accessing controlled state keys (l1, l2, l3)
    if (localCategories.l1.length > 0) {
      currentFilters.categoryL1 = localCategories.l1.join(',');
    }
    // eslint-disable-next-line security/detect-object-injection -- safe: accessing controlled state keys (l1, l2, l3)
    if (localCategories.l2.length > 0) {
      currentFilters.categoryL2 = localCategories.l2.join(',');
    }
    // eslint-disable-next-line security/detect-object-injection -- safe: accessing controlled state keys (l1, l2, l3)
    if (localCategories.l3.length > 0) {
      currentFilters.categoryL3 = localCategories.l3.join(',');
    }

    // Include tags from local state (MVP 1.3 Phase 2)
    if (localTags.length > 0) {
      currentFilters.tags = localTags.join(',');
    } else {
      delete currentFilters.tags;
    }

    // Update all filters at once
    urlStateManager.updateFilters(currentFilters);
  };

  /**
   * Get display text for date range
   */
  const getDateDisplay = () => {
    if (localStartDate && localEndDate) {
      return `${formatDateDisplay(localStartDate)} - ${formatDateDisplay(localEndDate)}`;
    }
    return 'Any week';
  };

  /**
   * Get display text for guest count
   */
  const getGuestDisplay = () => {
    return localGuestCount === 1 ? '1 guest' : `${localGuestCount} guests`;
  };

  /**
   * Get display text for activity types
   */
  const getActivityDisplay = () => {
    if (localActivities.length === 0) {return 'Any type';}
    if (localActivities.length === 1) {return localActivities[0].name;}
    return `${localActivities.length} selected`;
  };

  return {
    // State
    locationInput,
    localStartDate,
    localEndDate,
    localGuestCount,
    localActivities,
    localCategories,  // MVP 1.3 Phase 2
    localTags,        // MVP 1.3 Phase 2

    // Handlers
    handleLocationChange,
    handleLocationSelect,
    handleDateRangeSelect,
    handleGuestCountChange,
    handleActivityChange,
    handleCategorySelect,      // MVP 1.3 Phase 2
    handleCategoryRemove,      // MVP 1.3 Phase 2
    handleTagToggle,           // MVP 1.3 Phase 2
    handleClearCategories,     // MVP 1.3 Phase 2
    handleClearTags,           // MVP 1.3 Phase 2
    handleSearch,

    // Display helpers
    getDateDisplay,
    getGuestDisplay,
    getActivityDisplay,

    // Utilities
    formatDateForAPI,
    formatDateDisplay,
  };
}
